import React, { useEffect, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { useFontSize } from '../../contexts/FontSizeContext';
import { useAuth } from '../../contexts/AuthContext';
import { getPageTitleSizeClass, getSectionHeadingSizeClass, getTextSizeClass } from '../../utils/fontSizeUtils';
import { sessionSaveService } from '../../services/sessionSaveService';

const Pricing: React.FC = () => {
  const { fontSize } = useFontSize();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if coming from upgrade button
  const isFromUpgrade = location.state?.fromUpgrade === true;
  
  // Use useLayoutEffect to scroll to top before browser paints
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, location.key]);
  
  useEffect(() => {
    document.title = 'Stock Table - Pricing';
  }, []);

  const handleRegisterFree = () => {
    // Try to use sessionSaveService first (like Header Sign-Up button does)
    // This ensures we get fresh, properly formatted data
    let sessionSaved = false;
    const sessionData = sessionSaveService.getSessionData();
    
    if (sessionData && sessionData.tickerList && sessionData.tickerList.length > 0) {
      sessionSaved = sessionSaveService.saveCurrentSessionToStorage();
    } else {
      // Fall back to sessionStorage (user might have navigated away from list page)
      const tickerListStr = sessionStorage.getItem('stocktable-ticker-list');
      const listName = sessionStorage.getItem('stocktable-list-name');
      
      if (tickerListStr) {
        try {
          const tickerList = JSON.parse(tickerListStr);
          if (Array.isArray(tickerList) && tickerList.length > 0) {
            // Ensure data is properly formatted in sessionStorage (consistent with Save button)
            const finalListName = listName || 'Main List';
            sessionStorage.setItem('stocktable-ticker-list', JSON.stringify(tickerList));
            sessionStorage.setItem('stocktable-list-name', finalListName);
            
            // Note: hiddenStatistics should already be in sessionStorage from list pages
            // We don't need to modify it, just ensure it's preserved
            
            sessionSaved = true;
          }
        } catch (e) {
          console.error('Failed to parse ticker list from sessionStorage:', e);
        }
      }
    }
    
    if (sessionSaved) {
      navigate('/signup', {
        state: {
          fromSignUp: true,
          returnPath: '/daily',
        },
      });
    } else {
      navigate('/signup');
    }
  };

  const isSubscribeBlocked = isAuthenticated && user?.disallowProSubscription === true;

  const handleProButtonClick = () => {
    if (isSubscribeBlocked) return;
    if (isAuthenticated && !user?.hasProSubscription) {
      navigate('/order-summary', {
        state: { freeTrialEligible: !user?.freeTrialUsedAt },
      });
      return;
    }
    // Not logged in: save session and navigate to signup
    handleSubscribeNowRedirect();
  };

  const handleSubscribeNowRedirect = () => {
    let sessionSaved = false;
    const sessionData = sessionSaveService.getSessionData();

    if (sessionData && sessionData.tickerList && sessionData.tickerList.length > 0) {
      sessionSaved = sessionSaveService.saveCurrentSessionToStorage();
    } else {
      const tickerListStr = sessionStorage.getItem('stocktable-ticker-list');
      const listName = sessionStorage.getItem('stocktable-list-name');

      if (tickerListStr) {
        try {
          const tickerList = JSON.parse(tickerListStr);
          if (Array.isArray(tickerList) && tickerList.length > 0) {
            const finalListName = listName || 'Main List';
            sessionStorage.setItem('stocktable-ticker-list', JSON.stringify(tickerList));
            sessionStorage.setItem('stocktable-list-name', finalListName);
            sessionSaved = true;
          }
        } catch (e) {
          console.error('Failed to parse ticker list from sessionStorage:', e);
        }
      }
    }

    if (sessionSaved) {
      navigate('/signup', {
        state: { fromSignUp: true, returnPath: '/daily', fromFreeTrialIntent: true },
      });
    } else {
      navigate('/signup', { state: { fromFreeTrialIntent: true } });
    }
  };

  return (
    <div className="min-h-screen bg-background-light dark:bg-background flex flex-col">
      <Header />
      
      <main className={`flex-grow flex flex-col items-center justify-center px-4 ${isFromUpgrade ? 'py-8' : 'py-16'} sm:px-6 lg:px-8`}>
        {!isFromUpgrade && (
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className={`${getPageTitleSizeClass(fontSize)} md:text-5xl font-bold text-text-light-primary dark:text-text-primary mb-6 tracking-tight`}>
              Simple, Transparent Pricing
            </h1>
            <p className={`text-lg md:text-xl text-text-light-secondary dark:text-text-secondary leading-relaxed ${getTextSizeClass(fontSize)}`}>
              Choose the plan that fits your investing needs. <br className="hidden sm:inline"/>Unlock powerful indicators and unlimited potential with Pro.
            </p>
          </div>
        )}

        <div className={`grid ${isFromUpgrade ? 'grid-cols-1 justify-items-center' : 'grid-cols-1 md:grid-cols-2'} gap-8 max-w-5xl w-full ${isFromUpgrade ? 'mt-0' : ''}`}>
          {/* Free Plan - Hide when coming from upgrade button */}
          {!isFromUpgrade && (
          <div className="bg-surface-light dark:bg-background-card rounded-xl shadow-lg border border-border-light dark:border-border-color flex flex-col relative overflow-hidden transition-transform hover:-translate-y-1 duration-300">
            <div className="p-8 flex-grow">
              <h3 className={`${getSectionHeadingSizeClass(fontSize)} text-2xl font-bold text-text-light-primary dark:text-text-primary mb-2`}>
                Free Plan
              </h3>
              <div className="flex items-baseline mb-6">
                <span className="text-4xl font-extrabold text-text-light-primary dark:text-text-primary">$0</span>
                <span className={`ml-2 text-text-light-secondary dark:text-text-secondary font-medium ${getTextSizeClass(fontSize)}`}>
                  / forever
                </span>
              </div>
              <p className={`text-text-light-secondary dark:text-text-secondary mb-8 ${getTextSizeClass(fontSize)}`}>
              Perfect for getting started with watch lists and viewing basic statistics.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-primary dark:text-text-primary ${getTextSizeClass(fontSize)}`}>
                    Up to <span className="font-bold">3 lists</span>
                  </span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-primary dark:text-text-primary ${getTextSizeClass(fontSize)}`}>
                    Up to <span className="font-bold">10 tickers</span> per list
                  </span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-primary dark:text-text-primary ${getTextSizeClass(fontSize)}`}>
                    Track up to <span className="font-bold">20 total tickers</span>
                  </span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-primary dark:text-text-primary ${getTextSizeClass(fontSize)}`}>
                    Limited set of basic indicators
                  </span>
                </li>
                <li className="flex items-start opacity-50">
                  <svg className="w-6 h-6 text-text-light-secondary dark:text-text-secondary mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-secondary dark:text-text-secondary ${getTextSizeClass(fontSize)}`}>
                    Advanced technicals & fundamentals
                  </span>
                </li>
              </ul>
            </div>
            {!(isAuthenticated && user?.hasProSubscription) && (
              <div className="p-8 pt-0 mt-auto">
                <button
                  onClick={handleRegisterFree}
                  disabled={isAuthenticated && !user?.hasProSubscription}
                  className={`w-full py-3 px-6 border-2 border-border-light dark:border-border-color ${
                    isAuthenticated && !user?.hasProSubscription
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:border-primary dark:hover:border-highlight-gold'
                  } text-text-light-primary dark:text-text-primary font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-highlight-gold dark:focus:ring-offset-gray-900 ${getTextSizeClass(fontSize)}`}
                >
                  {isAuthenticated && !user?.hasProSubscription ? 'Your Current Plan' : 'Register Free'}
                </button>
              </div>
            )}
          </div>
          )}

          {/* Pro Plan */}
          <div className={`bg-surface-light dark:bg-background-card rounded-xl shadow-xl border-2 border-primary dark:border-highlight-gold relative flex flex-col overflow-hidden transition-transform hover:-translate-y-1 duration-300 ${isFromUpgrade ? 'w-full max-w-md' : ''}`}>
            <div className="absolute top-0 right-0 bg-primary dark:bg-highlight-gold text-gray-900 text-xs font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wider">
              Most Popular
            </div>
            <div className="p-8 flex-grow">
              <h3 className={`${getSectionHeadingSizeClass(fontSize)} text-2xl font-bold text-text-light-primary dark:text-text-primary mb-2`}>
                Pro Plan
              </h3>
              <div className="flex items-baseline mb-6">
                <span className="text-4xl font-extrabold text-text-light-primary dark:text-text-primary">US$4.99</span>
                <span className={`ml-2 text-text-light-secondary dark:text-text-secondary font-medium ${getTextSizeClass(fontSize)}`}>
                  / month
                </span>
              </div>
              <p className={`text-text-light-secondary dark:text-text-secondary mb-8 ${getTextSizeClass(fontSize)}`}>
              For users who need more watch lists and access to additional indicators.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-primary dark:text-highlight-gold mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-primary dark:text-text-primary ${getTextSizeClass(fontSize)}`}>
                    Up to <span className="font-bold">10 lists</span>
                  </span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-primary dark:text-highlight-gold mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-primary dark:text-text-primary ${getTextSizeClass(fontSize)}`}>
                    Up to <span className="font-bold">20 tickers</span> per list
                  </span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-primary dark:text-highlight-gold mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-primary dark:text-text-primary ${getTextSizeClass(fontSize)}`}>
                    Track up to <span className="font-bold">100 total tickers</span>
                  </span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-primary dark:text-highlight-gold mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-primary dark:text-text-primary font-medium ${getTextSizeClass(fontSize)}`}>
                    All Premium Indicators:
                  </span>
                </li>
                <li className="flex items-start pl-8">
                  <ul className="list-disc space-y-1 text-text-light-secondary dark:text-text-secondary">
                    <li className={getTextSizeClass(fontSize)}>Fibonacci Levels & MACD</li>
                    <li className={getTextSizeClass(fontSize)}>EV/FCF Ratio, ROIC & Operating Cash Flow Ratio</li>
                    <li className={getTextSizeClass(fontSize)}>Free Cash Flow Yield, Cash Flow Margin</li>
                    <li className={getTextSizeClass(fontSize)}>Pros & Cons for Investing</li>
                  </ul>
                </li>
              </ul>
            </div>
            <div className="p-8 pt-0 mt-auto space-y-3">
              <button
                onClick={handleProButtonClick}
                disabled={isAuthenticated && (user?.hasProSubscription || isSubscribeBlocked)}
                className={`w-full py-3 px-6 bg-primary dark:bg-highlight-gold ${
                  isAuthenticated && (user?.hasProSubscription || isSubscribeBlocked)
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:opacity-90'
                } text-gray-900 font-bold rounded-lg shadow-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-highlight-gold dark:focus:ring-offset-gray-900 ${getTextSizeClass(fontSize)}`}
              >
                {isAuthenticated && user?.hasProSubscription
                  ? 'Your Current Plan'
                  : isSubscribeBlocked
                    ? 'Subscribe Now'
                    : !isAuthenticated || !user?.freeTrialUsedAt
                      ? 'Start Free Trial Now'
                      : 'Subscribe Now'}
              </button>
              {isAuthenticated && user?.hasProSubscription && (
                <button
                  onClick={() => navigate('/manage-billing')}
                  className={`w-full py-2 px-6 border border-border-light dark:border-border-color text-text-light-primary dark:text-text-primary font-semibold rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${getTextSizeClass(fontSize)}`}
                >
                  Manage Billing
                </button>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Pricing;

